'use client';

import { BigNum, SpotMarketConfig } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { notify } from 'src/utils/notifications';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/VelocityStore/helpers';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';

export function useTransferCollateral() {
	const velocityClient = useVelocityClient();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const accountsGet = useVelocityAccountsStore((s) => s.get);
	const getTxParams = useGetTxParams();

	// TODO: abstract into @velocity-exchange/common
	const transferCollateralAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			amount: BigNum,
			spotMarket: SpotMarketConfig,
			fromAccountUserId: number,
			toAccountUserId: number
		) => {
			invariant(velocityClient, 'driftClient is required');
			const ix = await velocityClient.getTransferDepositIx(
				amount.val,
				spotMarket.marketIndex,
				fromAccountUserId,
				toAccountUserId
			);

			await wallet.signAndSendTransaction(
				ix,
				getTxParams({
					marketId: MarketId.createSpotMarket(spotMarket.marketIndex),
					simulatedComputeUnitMultiplier: Env.computeUnitBufferMultiplier + 0.3,
				})
			);
		},
		[velocityClient, getTxParams]
	);

	const wrappedTransferCollateral = useSponsoredSignVelocityActionWithFallback(
		transferCollateralAction
	);

	const transferCollateral = useCallback(
		async (
			amount: BigNum,
			spotMarket: SpotMarketConfig,
			fromAccountKey: string,
			toAccountKey: string
		) => {
			if (!velocityClient || !walletAdapter) return;

			const accountsState = accountsGet();
			const fromAccount = accountsState.accounts[fromAccountKey];
			const toAccount = accountsState.accounts[toAccountKey];

			if (!fromAccount?.client || !toAccount?.client) {
				return;
			}

			const fromUser = fromAccount.client;

			const toastId = `transfer-${amount.toString()}-${Date.now()}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedTransferCollateral(
					amount,
					spotMarket,
					fromAccount.userId,
					toAccount.userId
				);

				notify({
					type: 'success',
					id: toastId,
					updatePrevious: true,
				});

				updateAfterTx(velocityClient, fromUser);

				return true;
			} catch (e: any) {
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[velocityClient, walletAdapter, accountsGet, wrappedTransferCollateral]
	);

	return { transferCollateral };
}
